import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { TYPEFORM_ORDINATION_URL } from '../api/ordained';

import { useDashboard } from './dashboardProvider';

type PCOContextType = {
  pcoInfoModalOpen: boolean;
  setPcoInfoModalOpen: (value: boolean) => void;
  pcoApplicationModalOpen: boolean;
  setPcoApplicationModalOpen: (value: boolean) => void;
  pcoSuccessModalOpen: boolean;
  setPcoSuccessModalOpen: (value: boolean) => void;
  openPcoEntry: () => void;
};

const PCOContext = createContext<PCOContextType>({
  pcoInfoModalOpen: false,
  setPcoInfoModalOpen: () => undefined,
  pcoApplicationModalOpen: false,
  setPcoApplicationModalOpen: () => undefined,
  pcoSuccessModalOpen: false,
  setPcoSuccessModalOpen: () => undefined,
  openPcoEntry: () => undefined,
});

export const PCOProvider = ({ children }: { children: React.ReactNode }) => {
  const [pcoInfoModalOpen, setPcoInfoModalOpen] = useState(false);
  const [pcoApplicationModalOpen, setPcoApplicationModalOpen] = useState(false);
  const [pcoSuccessModalOpen, setPcoSuccessModalOpen] = useState(false);
  const { ordained } = useDashboard();

  // Refunded orders always open the locked summary. Other pre-cutoff
  // ministers keep the legacy external Typeform flow.
  const openPcoEntry = useCallback(() => {
    if (ordained?.status === 'Refunded') {
      setPcoApplicationModalOpen(true);
      return;
    }
    if (ordained?.flow === 'typeform') {
      window.open(TYPEFORM_ORDINATION_URL, '_blank');
      return;
    }
    setPcoInfoModalOpen(true);
  }, [ordained?.flow, ordained?.status]);

  // Bridge for non-React callers (e.g. onAfterCompletedClickHelper).
  useEffect(() => {
    const handler = () => openPcoEntry();
    window.addEventListener('pco:openInfoModal', handler);
    return () => window.removeEventListener('pco:openInfoModal', handler);
  }, [openPcoEntry]);

  return (
    <PCOContext.Provider
      value={{
        pcoInfoModalOpen,
        setPcoInfoModalOpen,
        pcoApplicationModalOpen,
        setPcoApplicationModalOpen,
        pcoSuccessModalOpen,
        setPcoSuccessModalOpen,
        openPcoEntry,
      }}
    >
      {children}
    </PCOContext.Provider>
  );
};

export const usePCO = () => useContext(PCOContext);
